import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from 'components/common/Loader';
import { projectUserRole, showDropdown, Project } from 'redux/projectSlice';
import { useGetProjectByIdMutation } from 'redux/services/project';
import { Steps } from 'components/common/Steps';
import { STEPS } from './constants';
import { ProjectDetails } from './components/ProjectDetails';
import { ChecklistDetails } from './components/ChecklistDetails';
import { Submission } from './components/Submission';
import { Review } from './components/Review';
import Modal from 'components/common/Modal/Modal';
import {
  useCreateInspectionMutation,
  useGetInspectionByIdMutation,
  useUpdateInspectionPatchMutation,
} from 'redux/services/inspection';
import toast from 'react-hot-toast';
import {
  setChecklistDetails,
  setProjectDetails,
  setSubmissionDetails,
} from 'redux/conductInspectionSlice';
import { useGetChecklist } from 'hooks/useGetChecklist';
import {
  getInspectionDataForApi,
  getProjectDetailsFromInspectionData,
} from 'pages/utils';
import './ConductInspection.scss';
import { useTranslation } from 'react-i18next';

const ConductInspection = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId, inspectionId, step = '1' } = useParams();
  const [
    getAllInspectionDetails,
    { data: inspection, isLoading: getInspectionIsLoading },
  ] = useGetInspectionByIdMutation();
  const [updateInspectionPatch, { isLoading: updateInspectionLoading }] =
    useUpdateInspectionPatchMutation();
  const [createInspection, { isLoading: isCreateInspectionLoading }] =
    useCreateInspectionMutation();
  const [newIncidentId, setNewIncidentId] = useState('');
  const [getProjectById, { data, isLoading }] = useGetProjectByIdMutation();
  const [project, setProject] = useState<Project | null>(null);
  const navigate = useNavigate();
  const checklist = useGetChecklist();

  useEffect(() => {
    return () => {
      dispatch(setProjectDetails(null));
    };
  }, [dispatch]);

  useEffect(() => {
    if (project && checklist && inspection) {
      dispatch(
        setProjectDetails(
          getProjectDetailsFromInspectionData(inspection, checklist, project),
        ),
      );
      dispatch(setChecklistDetails(inspection.response));
      dispatch(
        setSubmissionDetails({
          submissionDocuments: inspection.submissionDocuments ?? [],
          drawingTitle: inspection.drawingTitle ?? [],
          doerImages: inspection.doerImages ?? [],
          generalFeedback: inspection.generalFeedback,
          satisfactionLevel: inspection.satisfactionLevel,
          eSign: inspection.eSign,
          status: inspection.status,
          closeByDate: inspection.closeByDate,
        }),
      );
    }
  }, [project, checklist, inspection, inspectionId, dispatch]);

  const fetchInspection = useCallback(async () => {
    await getAllInspectionDetails({
      projectId,
      id: inspectionId,
    });
  }, [getAllInspectionDetails, inspectionId, projectId]);

  useEffect(() => {
    if (inspectionId && !inspection) {
      fetchInspection();
    }
  }, [fetchInspection, inspectionId, inspection]);

  useEffect(() => {
    if (!data) {
      getProjectById(projectId);
    }
  }, [data, getProjectById, projectId]);

  useEffect(() => {
    dispatch(showDropdown(true));
    if (data) {
      dispatch(projectUserRole(data.role));
      setProject(data);
    }
    return () => {
      dispatch(showDropdown(false));
    };
  }, [data, dispatch]);

  const handleProjectDetailsSubmit = async (values: any) => {
    const payload = getInspectionDataForApi(values);
    if (!values.projectUUID && !inspectionId) {
      const res: any = await createInspection(payload);
      if (res.data) {
        navigate(`/project/${projectId}/conduct-inspection/${res.data._id}/2`);
        toast.success(t('save_success'));
        dispatch(
          setProjectDetails({
            ...values,
            _id: res.data._id,
          }),
        );
      } else {
        toast.error(res.error?.data?.message ?? t('something_went_wrong'));
      }
    } else {
      await updateInspectionPatch({
        projectId,
        inspectionId,
        page: '1',
        body: {
          ...payload,
          status: 'DRAFT',
        },
      });

      toast.success(t('save_success'));
      navigate(`/project/${projectId}/conduct-inspection/${inspectionId}/2`);
    }
  };

  const handleChecklistDetailsSubmit = async (payload: any) => {
    const res: any = await updateInspectionPatch(payload);

    if (res.data) {
      toast.success(t('save_success'));
      navigate(`/project/${projectId}/conduct-inspection/${inspectionId}/3`);
    } else {
      toast.error(res.error?.data?.message ?? t('something_went_wrong'));
    }
  };

  const handleSubmission = () => {
    navigate(`/project/${projectId}/conduct-inspection/${inspectionId}/4`);
  };

  const handleReviewSubmit = async (payload: any) => {
    const res: any = await updateInspectionPatch(payload);

    if (res.data) {
      setNewIncidentId(res.data.displayId ?? inspectionId);
    } else {
      toast.error(res.error?.data?.message ?? t('something_went_wrong'));
    }
  };

  const handleSubmit = () => {
    setNewIncidentId('');
    navigate(`/project/${projectId}/inspection`);
  };

  const renderPage = () => {
    switch (step) {
      case '1':
        return (
          <ProjectDetails
            project={project!}
            onSubmit={handleProjectDetailsSubmit}
          />
        );
      case '2':
        return (
          <ChecklistDetails
            project={project!}
            onSubmit={handleChecklistDetailsSubmit}
          />
        );
      case '3':
        return <Submission project={project!} onSubmit={handleSubmission} />;
      default:
        return <Review project={project!} onSubmit={handleReviewSubmit} />;
    }
  };

  return (
    <div className="conduct-inspection d-flex">
      <div className="page-title">
        <h2 className="page-title">{t('conduct_inspection')}</h2>
      </div>
      <Steps
        steps={STEPS}
        activeStep={Number(step)}
        enableNavigatingPreviousSteps
        baseLink={`/project/${projectId}/conduct-inspection/${
          inspectionId || ''
        }`}
      />
      <div className="d-flex flex-column setup-container">
        {project && renderPage()}
        {(isLoading ||
          isCreateInspectionLoading ||
          getInspectionIsLoading ||
          updateInspectionLoading) && <Loader />}
      </div>
      <Modal
        show={newIncidentId?.length > 0}
        withIcon
        type={'success'}
        title=""
        modalClassName="checklist-modal"
        footerContentAlignment="center"
        withIconBodyText={
          <div className="d-flex flex-column">
            <span>
              <b>{t('successfull')}</b>
            </span>
            <span className="h6">{t('inspection_id')}</span>
            <span className="h6">{inspection?.displayId}</span>
          </div>
        }
        displayCloseButton={false}
        submitBtnText={t('btn_ok')}
        hideCancelBtn
        onSubmit={handleSubmit}
        onClose={() => {}}
      />
    </div>
  );
};

export default ConductInspection;
